<script setup lang="ts">
import { ref } from "vue";
import {
  getCategoryRoute,
  getTranslatedProperty,
} from "@shopware-pwa/helpers-next";
import { getProductRoute } from "@shopware-pwa/helpers-next";

const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const { t } = useI18n();

const props = defineProps<{
  recipeData: any;
}>();
const recipe = props.recipeData;
const isMobile = ref(false);

function getTime(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  let result = "";
  if (hours > 0) {
    result += t("recipe.time.hour", { count: hours });
  }
  if (remainingMinutes > 0) {
    if (hours > 0) {
      result += " ";
    }
    result += t("recipe.time.minute", { count: remainingMinutes });
  }

  return result;
}
</script>

<template>
  <section class="print-pdf">
    <div class="recepies-detail px-4 pt-4">
      <div class="container mx-auto py-3 min-w-[840px]">
        <h3 class="text-3xl font-bold mb-4 mb-6 text-center -ml-[100px] py-5">
          {{ recipe?.title }}
        </h3>
        <hr class="border-t border-gray-300 mb-6" />

        <div class="flex justify-between flex-nowrap flex-row pl-10">
          <div class="w-[490px] rounded-lg mb-8">
            <NuxtImg
              :src="recipe?.cover?.media?.url"
              :alt="recipe?.title"
              :width="420"
              :height="420"
              class="w-[300px] h-[300px] rounded-lg object-cover"
              :modifiers="{ sizes: '420px' }"
              format="webp"
              densities="x1"
              no-srcset
            />

            <div class="wrap w-full mx-auto bg-white rounded-2xl">
              <h5 class="text-2xl text-dark-bg font-bold mb-5 mt-8">
                {{ $t("recipe.recommended") }}
              </h5>
              <ul>
                <li
                  v-for="products in recipe?.productRecommendations"
                  class="flex items-center mb-3"
                >
                  <i class="fa-solid fa-check"></i>
                  <RouterLink :to="formatLink(getProductRoute(products))">
                    <span class="ml-2">{{ products.name }}</span>
                  </RouterLink>
                </li>
              </ul>
            </div>
          </div>

          <!-- Content Section -->
          <div class="w-full p-0 pl-[6%]">
            <div class="grid grid-cols-1 gap-4 mb-4 mb-6 grid-cols-1">
              <div class="flex items-start" v-if="recipe?.portions">
                <div
                  class="w-10 h-10 flex items-center justify-center bg-snow rounded-full mr-4"
                >
                  <img src="assets/icon/user-red-icon.svg" alt="Icon 1" />
                </div>
                <div>
                  <p class="text-sm">
                    {{ $t("recipe.servings") }}
                  </p>
                  <p class="text-base font-bold nowarp">
                    {{ recipe?.portions }} {{ $t("recipe.persons") }}
                  </p>
                </div>
              </div>

              <div class="flex items-start" v-if="recipe?.totalTime">
                <div
                  class="w-10 h-10 flex items-center justify-center bg-snow rounded-full mr-4"
                >
                  <img src="assets/icon/clock-red-icon.svg" alt="Icon 2" />
                </div>
                <div>
                  <p class="text-sm">
                    {{ $t("recipe.cookTime") }}
                  </p>
                  <p class="text-base font-bold">
                    {{ getTime(recipe?.totalTime) }}
                  </p>
                </div>
              </div>

              <div class="flex items-start" v-if="recipe?.difficulty">
                <div
                  class="w-10 h-10 flex items-center justify-center bg-snow rounded-full mr-4"
                >
                  <img src="assets/icon/process-red-icon.svg" alt="Icon 3" />
                </div>
                <div>
                  <p class="text-sm">
                    {{ $t("recipe.difficulty") }}
                  </p>
                  <p class="text-base font-bold">
                    {{ t(recipe?.difficulty) }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Separator Line -->
            <hr class="border-t border-gray-300 mb-5" />
            <!-- Ingredients Section -->
            <h5 class="text-xl font-bold mb-4">
              {{ $t("recipe.ingredients") }}
            </h5>
            <template
              v-if="recipe?.ingredientLists"
              v-for="ingredients in recipe?.ingredientLists"
            >
              <ul
                class="mb-8 leading-[1px] text-sm font-semibold text-dark-bg"
              >
              <li class="text-lg font-bold mb-4"><i class="fa fa-cutlery mr-1"></i> {{ ingredients?.translated?.title }}</li>
                <template v-for="ingredientItem in ingredients.ingredients">
                  <li
                    v-if="ingredientItem.title"
                    class="mb-4 flex items-center"
                  >
                    <i class="fa-solid fa-check"></i>
                    <span class="ml-3">
                      {{ ingredientItem.quantity }}
                      <template v-if="$te(ingredientItem.unit)">
                        {{ $t(ingredientItem.unit) }}
                      </template>
                      <template v-else>
                        {{ ingredientItem.unit }}
                      </template>
                      {{ ingredientItem.title }}
                    </span>
                  </li>
                </template>
              </ul>
            </template>

            <!-- Separator Line -->
            <hr class="border-t border-gray-300 mb-4" />

            <h5
              class="text-lg text-xl font-bold mb-3"
              v-if="recipe?.categories"
            >
              {{ $t("recipe.tags") }}
            </h5>
            <div
              v-if="recipe?.categories"
              class="mb-8 text-sm font-semibold text-red-bg"
            >
              <template v-for="category in recipe?.categories">
                <NuxtLink
                  role="menuitem"
                  :to="formatLink(getCategoryRoute(category))"
                >
                  {{ getTranslatedProperty(category, "name") }}
                </NuxtLink>
                <span
                  v-if="
                    category !==
                    recipe?.categories[recipe?.categories.length - 1]
                  "
                  >,</span
                >
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="instruction bg-white instruction-print pt-10">
      <div class="container mx-auto relative py-3 px-4">
        <div class="title mb-4 text-center mb-10">
          <h4 class="text-3xl font-bold">
            {{ $t("recipe.cooking") }}
            <span class="text-red-bg">{{ $t("recipe.instructions") }}</span>
          </h4>
        </div>
        <div class="wrap mx-auto bg-white rounded-2xl p-9 :w-[794px]">
          <div class="accordion">
            <div
              v-for="(preparations, index) in recipe?.preparations"
              :key="index"
              class="accordion-item border-b border-custom-grey bg-white"
            >
              <button
                class="accordion-header flex justify-between items-center w-full py-5 focus:outline-none bg-white"
              >
                <span
                  class="text-lg font-bold text-red-bg leading-normal uppercase"
                  >{{ preparations?.title }}</span
                >
                <svg
                  class="w-2 h-2 transform transition-transform accordion-icon rotate-180"
                  viewBox="0 0 8 5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.51984 4.17398L0.368716 1.02288C0.325249 0.979409 0.291016 0.930875 0.266016 0.877275C0.241016 0.823658 0.228516 0.766217 0.228516 0.70495C0.228516 0.582417 0.269949 0.475959 0.352816 0.385576C0.435666 0.295193 0.544874 0.25 0.680441 0.25H7.31884C7.45441 0.25 7.56362 0.295626 7.64647 0.386876C7.72933 0.478143 7.77076 0.584609 7.77076 0.706276C7.77076 0.736709 7.72397 0.842309 7.63037 1.02308L4.47944 4.174C4.40701 4.24643 4.33213 4.29932 4.25482 4.33265C4.17748 4.36598 4.09242 4.38265 3.99964 4.38265C3.90686 4.38265 3.8218 4.36598 3.74447 4.33265C3.66715 4.29932 3.59227 4.24643 3.51984 4.17398Z"
                    fill="#141414"
                  />
                </svg>
              </button>

              <div
                class="accordion-content pb-4 text-chinese-black block description-p"
              >
                <template
                  v-for="steps in preparations?.steps"
                  class="accordion-item border-b border-custom-grey bg-white"
                >
                  <div
                    class="text-lg font-normal leading-normal"
                    v-html="steps.description"
                  ></div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<style scoped>
.recipies-to-print {
  page-break-inside: avoid;
}
h3,
h5,
p,
ul,
li,
img,
button,
.description-p {
  page-break-inside: avoid;
}

hr {
  page-break-after: avoid;
}

.instruction-print {
  page-break-before: always;
}
</style>
